import "./FincaGisMapModal.css";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { Polygon } from "geojson";
import { BaseModalComponent } from "../../../shared/components/modal/BaseModalComponent";
import { FincaFichaResponse } from "../../../shared/response/terceros/FincaFichaResponse";
import { GOOGLE_MAPS_API_KEY, ORTOMOSAICO_PUBLIC_URI_PREFIX } from "../../../constantes";
import { storageTilesSasReadService } from "../../../shared/services/storage/StorageTilesSasReadService";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";

/** Tile transparente (1x1 gif) para tiles faltantes: evita el ícono de "imagen rota"
 *  en los bordes del ortomosaico -mismo criterio que `OrtomosaicoControl.ts` de Cliente-. */
const TRANSPARENT_TILE =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/**
 * Vista de solo lectura del AOI + lotes + conteo + ortomosaico (si está publicado)
 * sobre satélite de Google, a pantalla completa -antes vivía embebida como un div de
 * 280px dentro del detalle de finca; a pedido pasó a modal, más parecido a como lo
 * muestra Cliente-. Mismo patrón de tile URL que `AoiMapRender.ts` del Cliente
 * (`mt{s}.google.com/vt` con la API key real), no el plugin `leaflet.gridlayer.googlemutant`
 * (declarado en package.json pero sin uso real en ningún lado del sistema). El toggle de
 * ortomosaico replica `OrtomosaicoControl.ts` de Cliente, condensado acá -es el único lugar
 * de Plataforma que lo necesita, no amerita una clase reusable aparte-.
 */
export class FincaGisMapModal extends BaseModalComponent {
  private _map: L.Map | null = null;
  private _aoiLayer: L.GeoJSON | null = null;
  private _aoiVisible = false;
  private _lotesLayer: L.LayerGroup | null = null;
  /** Único que arranca prendido, a pedido -el resto arranca apagado-. */
  private _lotesVisible = true;
  private _ortomosaicoLayer: L.TileLayer | null = null;
  private _ortomosaicoVisible = false;
  private _conteoLayer: L.GeoJSON | null = null;
  private _conteoVisible = false;

  constructor(private readonly _finca: FincaFichaResponse) {
    super();
  }

  protected get title(): string { return this._finca.nombreFinca; }
  protected get subtitle(): string | undefined { return "AOI, lotes y conteo"; }
  protected get wrapBody(): boolean { return false; }

  protected renderBody(): string {
    return `<div class="cl-gis-map-modal-body" id="cl-gis-map-modal"></div>`;
  }

  protected renderFooter(): string {
    return "";
  }

  protected onAfterRender(): void {
    const container = document.getElementById("cl-gis-map-modal")!;
    const f = this._finca;

    const map = L.map(container, {
      center: [4.711, -74.0721],
      zoom: 6,
      maxZoom: 20,
      zoomControl: true,
      attributionControl: false,
    });
    this._map = map;

    L.tileLayer(
      `https://mt{s}.google.com/vt?lyrs=s&x={x}&y={y}&z={z}&key=${GOOGLE_MAPS_API_KEY}`,
      { maxZoom: 20, subdomains: ["0", "1", "2", "3"] },
    ).addTo(map);

    const bounds = L.latLngBounds([]);

    // Orden de botones a pedido: Lotes → AOI → Ortomosaico → Conteo (Leaflet apila
    // los controles de una misma esquina en el orden en que se agregan).
    const lotesConGeometria = (f.lotes ?? []).filter((l) => l.areaGeograficaPolygon?.coordinates?.length);
    if (lotesConGeometria.length > 0) {
      const sublayers = lotesConGeometria.map((lote) =>
        L.geoJSON(lote.areaGeograficaPolygon, {
          style: { fillColor: "transparent", color: "#38BDF8", weight: 2 },
        })
          // permanent (no sticky-on-hover): mismo criterio que LoteSplitMapRender de
          // Cliente -el nombre se lee de una, sin pasar el mouse por cada polígono-.
          .bindTooltip(this._escapeHtml(lote.nombreLote), {
            direction: "center",
            className: "cl-gis-lote-label",
            permanent: true,
          }),
      );
      // Único que arranca prendido, a pedido -el resto arranca apagado-.
      this._lotesLayer = L.layerGroup(sublayers).addTo(map);
      for (const sublayer of sublayers) bounds.extend(sublayer.getBounds());
      this._setupLotes(map);
    }

    const aoi = f.sateliteAreaGeografica as Polygon | null | undefined;
    if (aoi?.coordinates?.length) {
      // No se agrega al mapa acá: arranca apagado -ver _setupAoi-. Se construye
      // igual para poder incluirlo en fitBounds aunque esté apagado.
      this._aoiLayer = L.geoJSON(aoi, { style: { fillColor: "transparent", color: "#F97316", weight: 2.5 } });
      bounds.extend(this._aoiLayer.getBounds());
      this._setupAoi(map);
    }

    this._setupOrtomosaico(map, f);

    if (f.conteoMultiPoint?.coordinates?.length) {
      // No se agrega al mapa acá: arranca apagado, igual que el ortomosaico -ver
      // _setupConteo-. Se construye igual para poder incluirlo en fitBounds
      // aunque esté apagado (si no, la vista inicial no lo tendría en cuenta).
      this._conteoLayer = L.geoJSON(f.conteoMultiPoint, {
        pointToLayer: (_feature, latlng) => L.circleMarker(latlng, {
          radius: 2.5, color: "#F87171", fillColor: "#F87171", fillOpacity: 1, weight: 0,
        }),
      });
      bounds.extend(this._conteoLayer.getBounds());
      this._setupConteo(map);
    }

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [16, 16] });
    }

    // El contenedor ya tiene tamaño real -BaseModalComponent lo adjunta al DOM antes
    // de llamar este hook-, pero un invalidateSize de más no cuesta nada.
    requestAnimationFrame(() => map.invalidateSize());
  }

  public override dispose(): void {
    this._aoiLayer?.remove();
    this._aoiLayer = null;
    this._lotesLayer?.remove();
    this._lotesLayer = null;
    this._ortomosaicoLayer?.remove();
    this._ortomosaicoLayer = null;
    this._conteoLayer?.remove();
    this._conteoLayer = null;
    this._map?.remove();
    this._map = null;
    super.dispose();
  }

  // ── AOI ───────────────────────────────────────────────────

  /** Registra el botón de toggle del AOI (arriba a la derecha). Arranca
   *  apagado: `_aoiLayer` ya está armado (ver `onAfterRender`), solo falta
   *  agregarlo/quitarlo del mapa. */
  private _setupAoi(map: L.Map): void {
    const btn = L.DomUtil.create("button", "sx-btn sx-btn-outline sx-btn-sm cl-gis-aoi-btn");
    btn.type = "button";
    btn.innerHTML = '<i class="ph ph-navigation-arrow" aria-hidden="true"></i> AOI';
    L.DomEvent.disableClickPropagation(btn);
    L.DomEvent.on(btn, "click", () => this._toggleAoi(map, btn));

    const control = new L.Control({ position: "topright" });
    control.onAdd = () => btn;
    control.addTo(map);
  }

  private _toggleAoi(map: L.Map, btn: HTMLButtonElement): void {
    this._aoiVisible = !this._aoiVisible;
    if (this._aoiVisible) {
      this._aoiLayer?.addTo(map);
      btn.classList.add("cl-gis-aoi-btn--active");
    } else {
      this._aoiLayer?.remove();
      btn.classList.remove("cl-gis-aoi-btn--active");
    }
  }

  // ── Lotes ─────────────────────────────────────────────────

  /** Registra el botón de toggle de lotes (arriba a la derecha). Arranca
   *  prendido -a diferencia del resto-, `_lotesLayer` ya está en el mapa
   *  (ver `onAfterRender`). */
  private _setupLotes(map: L.Map): void {
    const btn = L.DomUtil.create("button", "sx-btn sx-btn-outline sx-btn-sm cl-gis-lotes-btn cl-gis-lotes-btn--active");
    btn.type = "button";
    btn.innerHTML = '<i class="ph ph-shapes" aria-hidden="true"></i> Lotes';
    L.DomEvent.disableClickPropagation(btn);
    L.DomEvent.on(btn, "click", () => this._toggleLotes(map, btn));

    const control = new L.Control({ position: "topright" });
    control.onAdd = () => btn;
    control.addTo(map);
  }

  private _toggleLotes(map: L.Map, btn: HTMLButtonElement): void {
    this._lotesVisible = !this._lotesVisible;
    if (this._lotesVisible) {
      this._lotesLayer?.addTo(map);
      btn.classList.add("cl-gis-lotes-btn--active");
    } else {
      this._lotesLayer?.remove();
      btn.classList.remove("cl-gis-lotes-btn--active");
    }
  }

  // ── Conteo ────────────────────────────────────────────────

  /** Registra el botón de toggle del conteo (arriba a la derecha). Arranca
   *  apagado: `_conteoLayer` ya está armado (ver `onAfterRender`), solo falta
   *  agregarlo/quitarlo del mapa. */
  private _setupConteo(map: L.Map): void {
    const btn = L.DomUtil.create("button", "sx-btn sx-btn-outline sx-btn-sm cl-gis-conteo-btn");
    btn.type = "button";
    btn.innerHTML = '<i class="ph ph-map-pin" aria-hidden="true"></i> Conteo';
    L.DomEvent.disableClickPropagation(btn);
    L.DomEvent.on(btn, "click", () => this._toggleConteo(map, btn));

    const control = new L.Control({ position: "topright" });
    control.onAdd = () => btn;
    control.addTo(map);
  }

  private _toggleConteo(map: L.Map, btn: HTMLButtonElement): void {
    this._conteoVisible = !this._conteoVisible;
    if (this._conteoVisible) {
      this._conteoLayer?.addTo(map);
      btn.classList.add("cl-gis-conteo-btn--active");
    } else {
      this._conteoLayer?.remove();
      btn.classList.remove("cl-gis-conteo-btn--active");
    }
  }

  // ── Ortomosaico ───────────────────────────────────────────

  /** Registra el botón de toggle (arriba a la derecha). Arranca apagado -no
   *  dispara la carga de la capa hasta que se prenda-. No hace nada si la
   *  finca no tiene ortomosaico publicado. */
  private _setupOrtomosaico(map: L.Map, f: FincaFichaResponse): void {
    if (!f.uriOrtomosaico) return;

    const btn = L.DomUtil.create("button", "sx-btn sx-btn-outline sx-btn-sm cl-gis-ortomosaico-btn");
    btn.type = "button";
    btn.innerHTML = '<i class="ph ph-map-trifold" aria-hidden="true"></i> Ortomosaico';
    L.DomEvent.disableClickPropagation(btn);
    L.DomEvent.on(btn, "click", () => void this._toggleOrtomosaico(map, f, btn));

    const control = new L.Control({ position: "topright" });
    control.onAdd = () => btn;
    control.addTo(map);
  }

  private async _toggleOrtomosaico(map: L.Map, f: FincaFichaResponse, btn: HTMLButtonElement): Promise<void> {
    this._ortomosaicoVisible = !this._ortomosaicoVisible;
    if (this._ortomosaicoVisible) {
      await this._addOrtomosaicoLayer(map, f);
    } else {
      this._ortomosaicoLayer?.remove();
      this._ortomosaicoLayer = null;
      btn.classList.remove("cl-gis-ortomosaico-btn--active");
    }
  }

  /** Pide el SAS (salvo que la URI ya sea de un contenedor público, ver
   *  `ORTOMOSAICO_PUBLIC_URI_PREFIX`), arma la capa de tiles y la agrega al
   *  mapa; si falla el SAS, revierte el estado del toggle. */
  private async _addOrtomosaicoLayer(map: L.Map, f: FincaFichaResponse): Promise<void> {
    const uri = f.uriOrtomosaico;
    if (!uri) return;
    const btn = document.querySelector<HTMLButtonElement>(".cl-gis-ortomosaico-btn");

    if (uri.startsWith(ORTOMOSAICO_PUBLIC_URI_PREFIX)) {
      this._ortomosaicoLayer = L.tileLayer(uri, {
        maxZoom: 20, maxNativeZoom: 19, errorTileUrl: TRANSPARENT_TILE,
      }).addTo(map);
      btn?.classList.add("cl-gis-ortomosaico-btn--active");
      return;
    }

    try {
      const sas = await storageTilesSasReadService.getAsync();
      // Se pudo apagar mientras resolvía el SAS.
      if (!this._ortomosaicoVisible) return;
      this._ortomosaicoLayer = L.tileLayer(`${uri}?${sas.sasToken}`, {
        maxZoom: 20, maxNativeZoom: 19, errorTileUrl: TRANSPARENT_TILE,
      }).addTo(map);
      btn?.classList.add("cl-gis-ortomosaico-btn--active");
    } catch (error) {
      this._ortomosaicoVisible = false;
      btn?.classList.remove("cl-gis-ortomosaico-btn--active");
      errorMessageComponent.addError("Error al cargar el ortomosaico", (error as Error).message);
    }
  }

  private _escapeHtml(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
  }
}
